import type { ComponentProps } from "react"
import cn from "@/v2-ui/utils/utils.cn"
import { errorToast } from "@/v2-ui/toast"
import Badge from "@/v2-backoffice-client/components/badge"
import Button, { ConfirmButton } from "@/v2-backoffice-client/components/button"
import { useUptimeConfigProviderDeleteMutation } from "@/v2-backoffice-client/uptime/configProvider/delete/uptime.configProvider.delete.mutation"
import { useUptimeConfigHostSuffixDeleteMutation } from "@/v2-backoffice-client/uptime/configHostSuffix/delete/uptime.configHostSuffix.delete.mutation"
import type { UptimeConfigQuery } from "@/v2-backoffice-client/uptime/config/__types__/uptime.config.query"

type Provider = UptimeConfigQuery["getStatusConfig"]["Providers"][number]
type HostSuffix = Provider["HostSuffixes"][number]

export type UptimeConfigProviderRowProps = ComponentProps<"li"> & {
  provider: Provider
  onEdit(): void
  onAddHostSuffix(): void
  onEditHostSuffix(hostSuffix: HostSuffix): void
}

function UptimeConfigProviderRow(props: UptimeConfigProviderRowProps) {
  const {
    provider,
    onEdit,
    onAddHostSuffix,
    onEditHostSuffix,
    className,
    ...liProps
  } = props

  const [delProvider, { loading: deleting, error: deleteError }] =
    useUptimeConfigProviderDeleteMutation()
  errorToast(deleteError ?? null, {
    title: "Failed to delete provider",
  })

  const [delHs, { error: hsDeleteError }] =
    useUptimeConfigHostSuffixDeleteMutation()
  errorToast(hsDeleteError ?? null, {
    title: "Failed to delete host suffix",
  })

  const sortedHs = [...provider.HostSuffixes].toSorted(
    (a, b) =>
      a.Priority - b.Priority ||
      // Longest first within a priority bucket so the resolution
      // order in the preview matches the SQL ORDER BY.
      b.HostSuffix.length - a.HostSuffix.length,
  )

  return (
    <li
      className={cn(
        "uptime-config-row",
        "uptime-config-row-stacked",
        !provider.IsEnabled && "uptime-config-row-disabled",
        className,
      )}
      {...liProps}
    >
      <div className="uptime-config-row-top">
        <div className="uptime-config-row-meta">
          <div className="uptime-config-row-title">
            <span>{provider.Name}</span>
            {!provider.IsEnabled && (
              <Badge variant="neutral" size="sm">
                Disabled
              </Badge>
            )}
          </div>
          <div className="uptime-config-row-subtitle">
            <code className="uptime-config-row-id">{provider.Id}</code>
            <span>· Priority {provider.Priority}</span>
            <span>
              · {provider.HostSuffixes.length} host suffix
              {provider.HostSuffixes.length === 1 ? "" : "es"}
            </span>
          </div>
        </div>
        <div className="uptime-config-row-actions">
          <Button variant="ghost" size="sm" iconSrc="pencil" onClick={onEdit}>
            Edit
          </Button>
          <ConfirmButton
            variant="ghost"
            size="sm"
            iconSrc="trash"
            content="Delete provider"
            confirmContent="Delete provider? Probes that pin this provider must be cleared first."
            isLoading={deleting}
            onConfirm={async () => {
              await delProvider({ variables: { id: provider.Id } })
            }}
          />
        </div>
      </div>

      <div className="uptime-config-host-suffix-list">
        {sortedHs.map((hs) => (
          <div key={hs.Id} className="uptime-config-host-suffix-row">
            <code className="uptime-config-host-suffix">{hs.HostSuffix}</code>
            <span className="uptime-config-host-suffix-priority">
              Priority {hs.Priority}
            </span>
            <div className="uptime-config-host-suffix-actions">
              <Button
                variant="ghost"
                size="tiny"
                iconSrc="pencil"
                onClick={() => {
                  onEditHostSuffix(hs)
                }}
              >
                Edit
              </Button>
              <ConfirmButton
                variant="ghost"
                size="tiny"
                iconSrc="trash"
                content="Delete host suffix"
                onConfirm={async () => {
                  await delHs({ variables: { hostSuffix: hs.HostSuffix } })
                }}
              />
            </div>
          </div>
        ))}

        <Button
          variant="ghost"
          size="tiny"
          iconSrc="plus"
          className="uptime-config-host-suffix-add"
          onClick={onAddHostSuffix}
        >
          Add host suffix
        </Button>
      </div>
    </li>
  )
}

export default UptimeConfigProviderRow
